/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./JobDescription.css";
import { fetchVacancy } from "../../Auth/saaedaApi";
import { auth, db } from "../../firebase";
import AuthModal from "../../Auth/AuthModal/AuthModal";
import { onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, query, where } from "firebase/firestore/lite";
import ConfettiExplosion from "react-confetti-explosion";
import { LanguageContext } from "../../context/LanguageContext";
import lang from "../translations";
import { getErrorMessage } from "../../utils/errorHelper";

const JobDescription = () => {
  const [job, setJob] = useState(null);
  const [jobExpired, setJobExpired] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [positionName, setPositionName] = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [fromApply, setFromApply] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false); // State to track if the user has already applied
  const [isExploding, setIsExploding] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const { appLanguage } = useContext(LanguageContext);

  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const id = params.get("id");
  if (id) {
    localStorage.setItem("id", id);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        checkIfApplied(currentUser.email);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkIfApplied = async (userEmail) => {
    try {
      const personalInfoQuery = query(collection(db, "personalInfo"), where("email", "==", userEmail));
      const querySnapshot = await getDocs(personalInfoQuery);
      const filteredDocs = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        if (data.Vacancy_Array && (data.Vacancy_Array.includes(id) || data.Vacancy_Array.includes(id.slice(0, -3)))) {
          filteredDocs.push({ id: doc.id, ...data });
        }
      });

      if (filteredDocs.length !== 0) {
        setAlreadyApplied(true);
      }
    } catch (error) {
      console.error("Error checking application status: ", error);
    }
  };

  const fetchJobData = async (id) => {
    const job = await fetchVacancy(id);
    const positionName = job.Position_Name__c;
    return { job, positionName };
  };

  useEffect(() => {
    const fetchJobDescription = async () => {
      if (id) {
        try {
          const { job, positionName } = await fetchJobData(id);
          setJob(job);
          setPositionName(positionName);

          const jobEndDate = job.Job_End_Date__c;
          const jobEndDateObj = new Date(jobEndDate);
          jobEndDateObj.setHours(0, 0, 0, 0);

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (jobEndDateObj < today || job.Stage__c !== "In-Progress") {
            setJobExpired(true);
          }
        } catch (error) {
          setError(error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchJobDescription();
  }, [id, location]);

  useEffect(() => {
    if (isExploding && !loading && user && alreadyApplied) {
      const audio = new Audio(process.env.PUBLIC_URL + "/sounds/pop.mp3");
      audio.play().catch((error) => {
        console.error("Error playing sound:", error);
      });
    }
  }, [isExploding, loading]);

  const handleApply = () => {
    if (user) {
      if (user.emailVerified) {
        navigate("/wizard", {
          state: { id: id, positionName: positionName },
        });
      } else {
        navigate("/verification", {
          state: { email: user.email },
        });
      }
    } else {
      setFromApply(true);
      setIsAuthModalOpen(true);
    }
  };

  if (loading) {
    return (
      <div
        className="card m-3 mx-auto rounded-4 shadow responsive-card-width"
        dir={appLanguage === "en" ? "ltr" : "rtl"}
      >
        <div className="card-body placeholder-glow">
          <h5 className="card-title fs-3 placeholder col-4"> </h5>
          <br />
          <h6 className="card-subtitle mb-2 text-muted placeholder col-2"> </h6>
          <div className="row mb-1 mx-2">
            <span className="placeholder col"></span>&nbsp;
            <span className="placeholder col"></span>&nbsp;
            <span className="placeholder col"></span>&nbsp;
            <span className="placeholder col"></span>
          </div>
          <div className="row mb-1 mx-2">
            <span className="placeholder col"></span>&nbsp;
            <span className="placeholder col"></span>&nbsp;
            <span className="placeholder col"></span>&nbsp;
            <span className="placeholder col"></span>
          </div>
          <div className="row mb-1 mx-2">
            <span className="placeholder col-3"></span>&nbsp;
            <span className="placeholder col-3"></span>
          </div>

          <h2 className="placeholder col-3 mt-3"> </h2>
          <p className="placeholder col-12 p-5"></p>

          <h2 className="placeholder col-3"> </h2>
          <p className="placeholder col-12 p-5"></p>

          <h2 className="placeholder col-3"> </h2>
          <p className="placeholder col-12 p-5"></p>

          <p className="placeholder col-9 p-3"></p>

          <br />
          <p className="btn btn-dark disabled placeholder col-2" aria-disabled="true"></p>
        </div>
      </div>
    );
  }

  if (error) {
    // Pick a clear, human explanation (and matching icon) for what went wrong
    const t = lang[appLanguage];
    let errorInfo = { icon: "bi-exclamation-triangle", msg: t.errUnknown };
    if (error.response) {
      const status = error.response.status;
      if (status === 404) errorInfo = { icon: "bi-search", msg: t.errJobNotFound };
      else if (status === 429) errorInfo = { icon: "bi-hourglass-split", msg: t.errRateLimit };
      else errorInfo = { icon: "bi-server", msg: t.errServer };
    } else if (error.request) {
      errorInfo =
        error.code === "ECONNABORTED"
          ? { icon: "bi-hourglass-split", msg: t.errTimeout }
          : { icon: "bi-wifi-off", msg: t.errNetwork };
    }

    return (
      <div
        className="card m-3 mx-auto rounded-4 shadow responsive-card-width"
        dir={appLanguage === "en" ? "ltr" : "rtl"}
      >
        <div className="card-body text-center py-5">
          <div
            className="bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
            style={{ width: 72, height: 72 }}
          >
            <i className={`bi ${errorInfo.icon} fs-1`}></i>
          </div>
          <h4>{t.errLoadJobTitle}</h4>
          <p className="text-muted mb-4">{errorInfo.msg}</p>
          <button type="button" className="btn btn-success px-4" onClick={() => window.location.reload()}>
            <i className="bi bi-arrow-clockwise me-1"></i>
            {t.btnTryAgain}
          </button>
          <details className="mt-4 small text-muted">
            <summary style={{ cursor: "pointer" }}>{t.errTechDetails}</summary>
            <div className="mt-2 p-2 bg-light rounded text-break text-start" dir="ltr">
              {getErrorMessage(error)}
            </div>
          </details>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="card m-3 mx-auto rounded-4 shadow responsive-card-width">
        <div className="card-body">
          <div className="alert alert-warning" role="alert">
            {lang[appLanguage].warnNoJob}
          </div>
        </div>
      </div>
    );
  }

  if (jobExpired) {
    return (
      <div className="card m-3 mx-auto rounded-4 shadow responsive-card-width">
        <div className="card-body">
          <div className="alert alert-warning" role="alert">
            {lang[appLanguage].warnOfferExpired}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card m-3 mx-auto rounded-4 shadow responsive-card-width" dir={appLanguage === "en" ? "ltr" : "rtl"}>
      {alreadyApplied && (
        <div className="alert alert-success m-4 mb-1" role="alert">
          🎉 {lang[appLanguage].congrats} 🚀🌟
          {isExploding && (
            <div className="confetti-container">
              <ConfettiExplosion
                onComplete={() => setIsExploding(false)}
                force={0.6}
                duration={3000}
                particleCount={200}
                width={1600}
              />
            </div>
          )}
        </div>
      )}

      <div className="card-body">
        {/* Compact job summary card */}
        <div className="d-flex align-items-center mb-3">
          <div
            className="bg-success bg-opacity-10 text-success rounded-3 d-flex align-items-center justify-content-center me-3"
            style={{ width: 56, height: 56, flexShrink: 0 }}
          >
            <i className="bi bi-briefcase-fill fs-3"></i>
          </div>
          <div>
            <h5 className="card-title fs-3 mb-0">
              {appLanguage === "en"
                ? job.Position_Name__c
                : job.Position_Name_AR__c || job.Position_Name__c}
            </h5>
            <div className="text-muted">
              {appLanguage === "en" ? job.Position_Name_AR__c : job.Position_Name__c}
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-4">
          {job.Governorate__c && (
            <span className="badge rounded-pill text-bg-light border fs-6 fw-normal">
              <i className="bi bi-geo-alt me-1 text-success"></i>
              {job.Governorate__c}
            </span>
          )}
          {job.Department__c && (
            <span className="badge rounded-pill text-bg-light border fs-6 fw-normal">
              <i className="bi bi-building me-1 text-success"></i>
              {job.Department__c}
            </span>
          )}
          {job.Job_Type__c && (
            <span className="badge rounded-pill text-bg-light border fs-6 fw-normal">
              <i className="bi bi-clock me-1 text-success"></i>
              {job.Job_Type__c}
            </span>
          )}
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          {alreadyApplied ? (
            <p className="text-success mb-0 align-self-center">{lang[appLanguage].warnAlreadyAplied}</p>
          ) : (
            <button type="button" className="btn btn-success button px-5" onClick={handleApply}>
              {lang[appLanguage].btnApply}
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-success"
            onClick={() => setShowDetails((s) => !s)}
            aria-expanded={showDetails}
          >
            <i className={`bi ${showDetails ? "bi-chevron-up" : "bi-eye"} me-1`}></i>
            {showDetails ? lang[appLanguage].jdHideDetails : lang[appLanguage].jdViewDetails}
          </button>
        </div>

        {/* Full details — shown on demand */}
        {showDetails && (
          <div className="fade-in">
            <div className="card my-3 p-3 shadow-sm border-0 bg-light bg-opacity-50">
              <div
                dangerouslySetInnerHTML={{
                  __html: appLanguage === "en" ? job.Job_Description__c : job.Job_Description_AR__c,
                }}
              />
            </div>

            {(appLanguage === "en" ? job.Skills_Competencies__c : job.Skills_Competencies_AR__c) && (
              <div className="card my-3 p-3 shadow-sm border-0 bg-light bg-opacity-50">
                <div
                  dangerouslySetInnerHTML={{
                    __html: appLanguage === "en" ? job.Skills_Competencies__c : job.Skills_Competencies_AR__c,
                  }}
                />
              </div>
            )}

            {(appLanguage === "en" ? job.About_Us__c : job.About_Us_AR__c) && (
              <div className="card my-3 p-3 shadow-sm border-0 bg-light bg-opacity-50">
                <div
                  dangerouslySetInnerHTML={{ __html: appLanguage === "en" ? job.About_Us__c : job.About_Us_AR__c }}
                />
              </div>
            )}

            {/* Apply again at the end, so the reader doesn't scroll back up */}
            <div className="text-center my-4">
              {alreadyApplied ? (
                <p className="text-success mb-0">{lang[appLanguage].warnAlreadyAplied}</p>
              ) : (
                <button type="button" className="btn btn-success button px-5" onClick={handleApply}>
                  {lang[appLanguage].btnApply}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <AuthModal
        isOpen={isAuthModalOpen}
        onRequestClose={() => setIsAuthModalOpen(false)}
        fromApply={fromApply}
        id={id}
        positionName={positionName}
      />
    </div>
  );
};

export default JobDescription;
